import ExcelJS from "exceljs";
import saveAs from "file-saver";

import {
  ChildCollection,
  ChildWorksheet,
  ExportContext,
  ExportCorelationRecordsOption,
  ExportOptions,
  ParentExportRow,
  ParentWorksheet,
} from "../../../../types/types.export";
import {
  IAMUserState,
  JsonSchema,
  LandingPageOptions,
  RowData,
} from "../../../../types";

import { buildChildSheet, buildChildSheets } from "./childSheetBuilder";
import {
  buildParentExportRows,
  getFieldsToExportForBaseAndChild,
} from "./identifierBuilder";
import { applyHyperlinks } from "./hyperlinkBuilder";
import { buildParentSheet } from "./parentSheetBuilder";
import { getChildCollections } from "./schemaInspector";
import { populateWorksheet } from "./worksheetBuilder";
import { PARENT_AND_CHILD_MERGED_EXPORT_SETUPS } from "../../constants";

const createExportContext = ({
  schema,
  selected,
  iamUserState,
}: {
  schema: JsonSchema;
  selected: LandingPageOptions;
  iamUserState: IAMUserState;
}): ExportContext => ({
  exportFields: getFieldsToExportForBaseAndChild(schema),
  setupName: selected.setup.label,
  schema,
  iamUserState,
});

const createWorksheet = (
  workbook: ExcelJS.Workbook,
  sheet: ParentWorksheet | ChildWorksheet,
): ExcelJS.Worksheet => {
  const worksheet = workbook.addWorksheet(sheet.sheetName);

  populateWorksheet(worksheet, sheet.rows);

  return worksheet;
};

const saveWorkbook = async (
  workbook: ExcelJS.Workbook,
  fileName: string,
): Promise<void> => {
  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(new Blob([buffer]), `${fileName}.xlsx`);
};

const shouldExportMergedWorksheet = (
  schemaTitle: string,
  childCollections: ChildCollection[],
): boolean =>
  childCollections.length === 1 &&
  PARENT_AND_CHILD_MERGED_EXPORT_SETUPS.includes(schemaTitle);

export const exportRecordsToExcel = async ({
  fileName,
  schema,
  selected,
  rowDataList,
  iamUserState,
}: ExportOptions): Promise<void> => {
  const context = createExportContext({
    schema,
    selected,
    iamUserState,
  });

  const parentRows = buildParentExportRows(rowDataList, context);
  const childCollections = getChildCollections(schema);

  if (shouldExportMergedWorksheet(schema.title, childCollections)) {
    return exportMergedRecordsToExcel({
      fileName,
      context,
      childCollection: childCollections[0],
      parentRows,
    });
  }

  const workbook = new ExcelJS.Workbook();

  const parentSheet = buildParentSheet(
    parentRows,
    schema,
    childCollections,
    context,
  );

  const childSheets = buildChildSheets(parentRows, childCollections, context);

  const parentWorksheet = createWorksheet(workbook, parentSheet);

  childSheets.forEach((sheet) => createWorksheet(workbook, sheet));

  applyHyperlinks(parentWorksheet, parentSheet.hyperlinks, childSheets);

  await saveWorkbook(workbook, fileName);
};

export const exportCorelationRecordsToExcel = async ({
  fileName,
  schema,
  selected,
  rowData,
  corelationRows,
  propertyKey,
  iamUserState,
}: ExportCorelationRecordsOption): Promise<void> => {
  const workbook = new ExcelJS.Workbook();

  const context = createExportContext({
    schema,
    selected,
    iamUserState,
  });

  const childCollection = getChildCollections(schema).find(
    (collection) => collection.field === propertyKey,
  );

  if (!childCollection) {
    return;
  }

  const updatedRowData: RowData = {
    ...rowData,
    [propertyKey]: corelationRows,
  };

  const parentRows = buildParentExportRows([updatedRowData], context);

  const childSheet = buildChildSheet(parentRows, childCollection, context);

  createWorksheet(workbook, childSheet);

  await saveWorkbook(workbook, fileName);
};

// Builds a single worksheet that merges parent and child records into one sheet.
// Parent data is repeated for each child record.
// for setups having single correlation field

// its the same as downloading the child sheet if all the fields are mentioned in the identifiers

const exportMergedRecordsToExcel = async ({
  fileName,
  context,
  childCollection,
  parentRows,
}: {
  fileName: string;
  context: ExportContext;
  childCollection: ChildCollection;
  parentRows: ParentExportRow[];
}): Promise<void> => {
  const workbook = new ExcelJS.Workbook();

  const worksheet = buildChildSheet(parentRows, childCollection, context);

  worksheet.sheetName = context.setupName;

  createWorksheet(workbook, worksheet);

  await saveWorkbook(workbook, fileName);
};
